import { FormEvent, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'

import * as api from 'api/api'
import { useAuth } from 'auth/AuthContext'
import { Icon } from 'components/Icon'
import { Button, ErrorState, PasswordField, Spinner, TextField } from 'components/ui'
import { errorMessage, useAsync } from 'lib/hooks'

import { AuthShell } from './AuthShell'
import { postAuthPath } from './postAuthRedirect'
import { RoleBadge } from './RoleBadge'
import { roleLabel } from './roleLabels'

const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/

const COPY = {
  ar: {
    title: 'قبول الدعوة',
    subtitle: 'أكمل إعداد حسابك لتبدأ بإرسال التقارير اليومية للموقع.',
    invitedAs: 'أنت مدعو بصفة',
    toProject: 'للانضمام إلى مشروع',
    projectSuffix: '',
    invitedBy: (name: string, days: number) => `دعوة من ${name} · تنتهي خلال ${days} يوم`,
    name: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    passwordHint: '8 أحرف على الأقل، تتضمن حرفاً ورقماً.',
    confirm: 'تأكيد كلمة المرور',
    confirmMismatch: 'كلمتا المرور غير متطابقتين.',
    terms: 'أوافق على شروط الاستخدام وسياسة الخصوصية.',
    submit: 'قبول والمتابعة',
    invalidLink: 'رابط الدعوة غير صالح أو منتهي الصلاحية.',
  },
  en: {
    title: 'Accept your invitation',
    subtitle: 'Set up your account to start sending daily site reports.',
    invitedAs: "You're invited as",
    toProject: 'to the',
    projectSuffix: ' project',
    invitedBy: (name: string, days: number) => `Invited by ${name} · expires in ${days} days`,
    name: 'Full name',
    email: 'Email',
    password: 'Set password',
    passwordHint: 'At least 8 characters, with a letter and a number.',
    confirm: 'Confirm password',
    confirmMismatch: 'Passwords do not match.',
    terms: 'I agree to the Terms of use and the Privacy policy.',
    submit: 'Accept and continue',
    invalidLink: 'This invitation link is invalid or has expired.',
  },
} as const

const daysUntil = (iso: string) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000))

export const AcceptInvitationPage = () => {
  const { user, setSession } = useAuth()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const { data: invitation, error: loadError, loading } = useAsync(() => api.fetchInvitation(token), [token])

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  if (user) return <Navigate to={postAuthPath(user)} replace />

  const passwordValid = PASSWORD_RULE.test(password)
  const confirmValid = confirm.length > 0 && confirm === password
  const canSubmit = Boolean(token) && name.trim().length > 0 && passwordValid && confirmValid && agreed

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setSubmitError(null)
    try {
      const { token: sessionToken, user: me } = await api.acceptInvitation(token, name.trim(), password)
      setSession(sessionToken, me)
    } catch (caught) {
      setSubmitError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell>
      {(locale) => {
        const t = COPY[locale]

        if (loading && !invitation) return <Spinner />
        if (!token || loadError || !invitation) return <ErrorState message={t.invalidLink} />

        return (
          <>
            <span className="auth__wordmark">JoPath</span>
            <div className="auth__head">
              <h1>{t.title}</h1>
              <p>{t.subtitle}</p>
            </div>
            <div className="auth__invite">
              <span>
                {t.invitedAs} <RoleBadge role={invitation.role} locale={locale} /> {t.toProject}{' '}
                <strong>{invitation.projectName}</strong>
                {t.projectSuffix}
              </span>
              <small>
                {t.invitedBy(
                  invitation.invitedByRole
                    ? `${invitation.invitedByName}${locale === 'ar' ? '، ' : ', '}${roleLabel(invitation.invitedByRole, locale)}`
                    : invitation.invitedByName,
                  daysUntil(invitation.expiresAt)
                )}
              </small>
            </div>
            <form className="stack" onSubmit={submit} noValidate>
              {submitError ? (
                <div className="alert alert--error" role="alert">
                  {submitError}
                </div>
              ) : null}
              <TextField
                label={t.name}
                value={name}
                disabled={busy}
                onChange={(event) => setName(event.target.value)}
                required
              />
              <div className="field">
                <span className="field__label">{t.email}</span>
                <div className="field__control">
                  <input id="invite-email" className="input" dir="ltr" value={invitation.email} disabled readOnly />
                  <span className="field__adornment field__adornment--static" aria-hidden="true">
                    <Icon name="lock" size={16} />
                  </span>
                </div>
              </div>
              <PasswordField
                label={t.password}
                hint={t.passwordHint}
                dir="ltr"
                value={password}
                disabled={busy}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <PasswordField
                label={t.confirm}
                dir="ltr"
                value={confirm}
                error={confirm && !confirmValid ? t.confirmMismatch : undefined}
                disabled={busy}
                onChange={(event) => setConfirm(event.target.value)}
                required
              />
              <label className="checkbox">
                <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
                {t.terms}
              </label>
              <Button type="submit" variant="primary" busy={busy} disabled={!canSubmit} className="btn--block">
                {t.submit}
              </Button>
            </form>
          </>
        )
      }}
    </AuthShell>
  )
}
